import { Game } from "./engine/Game.js";
// Import player name utilities
import {
  getPlayerName,
  updatePlayerNameIfCurrent,
} from "./utils/playerName.js";
// Import Supabase service functions
import {
  updatePlayerName,
  getLeaderboard,
  getPublicLevel,
  getPublishedLevel,
} from "./utils/SupabaseService.js";
import { registerCommunityMap } from "./levels/maps/index.js";
import {
  isCommunityMapId,
  isCustomMapId,
  isNonLeaderboardMapId,
  parseShareRef,
  communityMapId,
  buildRuntimeMap,
} from "./levels/customMaps.js";

// Variables to store the last submission details for potential name update
let lastSubmittedScoreId = null;
let lastChallengeJwt = null;

// Function to handle fullscreen requests
function toggleFullScreen(element) {
  element = element || document.documentElement;

  // Special handling for iOS devices
  if (isIOS()) {
    // If we're on iOS in standalone mode (added to home screen), we can use special meta tags
    if (window.navigator.standalone) {
      return; // Already in "fullscreen" via home screen app
    }

    // Prompt user to add to home screen for true fullscreen experience
    const addToHomeScreenMsg = document.getElementById("add-to-home-screen");
    if (addToHomeScreenMsg) {
      addToHomeScreenMsg.style.display = "flex";
    }

    return;
  }

  // Standard fullscreen API for non-iOS devices
  if (
    !document.fullscreenElement &&
    !document.mozFullScreenElement &&
    !document.webkitFullscreenElement &&
    !document.msFullscreenElement
  ) {
    // Enter fullscreen
    if (element.requestFullscreen) {
      element.requestFullscreen();
    } else if (element.msRequestFullscreen) {
      element.msRequestFullscreen();
    } else if (element.mozRequestFullScreen) {
      element.mozRequestFullScreen();
    } else if (element.webkitRequestFullscreen) {
      element.webkitRequestFullscreen(Element.ALLOW_KEYBOARD_INPUT);
    }
  } else {
    // Exit fullscreen
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    }
  }
}

// Function to handle screen orientation
function lockScreenOrientation() {
  try {
    if (screen.orientation && screen.orientation.lock) {
      screen.orientation.lock("landscape").catch(function (error) {
        console.log("Screen orientation lock failed: ", error);
      });
    } else if (screen.lockOrientation) {
      screen.lockOrientation("landscape");
    } else if (screen.mozLockOrientation) {
      screen.mozLockOrientation("landscape");
    } else if (screen.msLockOrientation) {
      screen.msLockOrientation("landscape");
    }
  } catch (error) {
    console.log("Screen orientation lock failed: ", error);
  }
}

// Function to check device orientation and show/hide prompt
function checkOrientation() {
  const rotateDevice = document.getElementById("rotate-device");

  // Get orientation from screen orientation API if available
  const isPortrait = window.matchMedia("(orientation: portrait)").matches;

  // On mobile devices, show rotation message in portrait mode
  if (isPortrait) {
    if (rotateDevice) rotateDevice.classList.add("flex");
    if (rotateDevice) rotateDevice.classList.remove("hidden");
  } else {
    if (rotateDevice) rotateDevice.classList.add("hidden");
    if (rotateDevice) rotateDevice.classList.remove("flex");
  }
}

// Function to detect if user is on a mobile device
function isMobileDevice() {
  // Check for touch capability and screen size
  const hasTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
  const isSmallScreen = window.innerWidth <= 768;

  // Check for mobile user agent
  const isMobileUA =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

  // Check if it's an iOS device using our isIOS function
  const isIOSDevice = isIOS();

  // Modern iPads (iPadOS 13+) don't identify as "iPad" in their user agent strings anymore.
  // Instead, they report as macOS devices to get desktop experiences on websites.
  // This means the standard mobile UA test above would fail for newer iPads.
  // We need the explicit isIOSDevice check to catch these devices correctly.

  // Consider it mobile if:
  // 1. It has touch AND (is small screen OR has mobile UA), OR
  // 2. It's an iOS device (which covers iPads properly)
  const result = (hasTouch && (isSmallScreen || isMobileUA)) || isIOSDevice;
  console.log("isMobileDevice", result);
  return result;
}

// Function to detect iOS devices
function isIOS() {
  const userAgent = navigator.userAgent.toLowerCase();

  // More comprehensive detection for modern iOS/iPadOS devices
  return (
    /ipad|iphone|ipod/.test(userAgent) ||
    (userAgent.includes("mac") && "ontouchend" in document) ||
    (navigator.maxTouchPoints > 0 && /mac/i.test(userAgent)) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1) ||
    navigator.platform === "iPhone" ||
    navigator.platform === "iPad" ||
    navigator.platform === "iPod"
  );
}

// Function to detect standalone mode (when added to home screen)
function isStandaloneMode() {
  return (
    window.navigator.standalone || // iOS
    window.matchMedia("(display-mode: standalone)").matches || // Other browsers
    window.matchMedia("(display-mode: fullscreen)").matches // PWA
  );
}

// Apply device/standalone body classes and wire orientation handling. Shared by
// both boot paths: the game (?map=) branch and the main-menu hub (bare URL), so
// the hub gets the same is-mobile copy and rotation behavior as the game.
function initDeviceClasses() {
  if (isStandaloneMode()) {
    document.body.classList.add("standalone-mode");
  }
  if (isMobileDevice()) {
    document.body.classList.add("is-mobile");
    checkOrientation();
    window.addEventListener("resize", checkOrientation);
    window.addEventListener("orientationchange", () => {
      setTimeout(checkOrientation, 300); // wait for screen dims to settle
    });
    lockScreenOrientation();
  }
}

// Initialize the game when the window loads
// Replace the splash with a friendly "community map not found" message when a
// shared ?map=<username>/<slug> link can't be resolved. Built with textContent
// (never innerHTML) so a hostile pasted ref can't inject markup.
function showCommunityNotFound(ref) {
  document.getElementById("loading-overlay")?.classList.add("hidden");
  const splash = document.getElementById("splash-screen");
  if (!splash) {
    window.location.search = "";
    return;
  }
  splash.replaceChildren();
  const title = document.createElement("div");
  title.className = "dialog-title";
  title.textContent = "Map not found";
  const msg = document.createElement("div");
  msg.className = "text-sm md:text-base mb-6 opacity-80 text-purple";
  msg.textContent = `We couldn't find a community map at "${ref}".`;
  const home = document.createElement("button");
  home.className = "btn";
  home.textContent = "Back to Menu";
  home.addEventListener("click", () => {
    window.location.search = "";
  });
  const editor = document.createElement("button");
  editor.className = "btn btn-secondary ml-2.5";
  editor.textContent = "Level Editor";
  editor.addEventListener("click", () => {
    window.location.search = "?edit=";
  });
  splash.append(title, msg, home, editor);
  splash.classList.remove("hidden");
}

window.addEventListener("load", async () => {
  // Editor mode: ?edit=<custom-id> opens the canvas editor, ?edit= opens the
  // "My Levels" hub. The editor is a separate, self-contained screen — skip all
  // game wiring and load it lazily (its own code chunk) when requested.
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.has("edit")) {
    // The editor short-circuits all game wiring, including the code that hides
    // the black loading overlay — drop it here so the editor isn't covered.
    document.getElementById("loading-overlay")?.classList.add("hidden");
    const editorRoot = document.getElementById("editor-root");
    import("./editor/LevelEditor.js").then(({ LevelEditor }) => {
      new LevelEditor({
        rootEl: editorRoot,
        mapId: urlParams.get("edit"),
      }).mount();
    });
    return;
  }

  // Dev-only: ?dev= opens the core Sequence Editor (edits src/levels/*.js and
  // saves to disk via the dev server). import.meta.env.DEV is false in a
  // production build, so this whole branch — and its dynamic import — is dropped.
  if (import.meta.env.DEV && urlParams.has("dev")) {
    document.getElementById("loading-overlay")?.classList.add("hidden");
    const editorRoot = document.getElementById("editor-root");
    import("./editor/DevSequenceEditor.js").then(({ DevSequenceEditor }) => {
      new DevSequenceEditor({
        rootEl: editorRoot,
        route: urlParams.get("dev"),
      }).mount();
    });
    return;
  }

  // Main-menu hub: a bare URL (no ?map=) shows the level-select hub instead of
  // booting a game. Like the editor, it's a self-contained DOM screen mounted
  // into its own root — no Game/Three.js instance behind it. Selecting anything
  // in the hub navigates to ?map=<id> (a reload), which lands in the game path
  // below. ?map= deep links and share links skip this and boot straight in.
  if (!urlParams.has("map")) {
    document.getElementById("loading-overlay")?.classList.add("hidden");
    // The pause button is game-only chrome (always in the DOM, z-index above the
    // hub) — hide it so it doesn't float over the menu.
    document.getElementById("pause-button")?.classList.add("hidden");
    initDeviceClasses();
    const menuRoot = document.getElementById("menu-root");
    import("./ui/MainMenu.js").then(({ MainMenu }) => {
      new MainMenu({ rootEl: menuRoot }).mount();
    });
    return;
  }

  const gameContainer = document.getElementById("game-container");
  const scoreElement = document.getElementById("score");
  const gameOverElement = document.getElementById("game-over");
  const restartButton = document.getElementById("restart-button");
  const splashScreen = document.getElementById("splash-screen");
  const startButton = document.getElementById("start-button");
  const fullscreenButton = document.getElementById("fullscreen-button");
  const pauseButton = document.getElementById("pause-button");
  // Get references to the new UI elements
  const playerNameDisplay = document.getElementById("playerNameDisplay");
  const changeNameButton = document.getElementById("changeNameButton");
  const playerNameSection = document.getElementById("player-name-section"); // Get name section
  // Leaderboard Elements
  const leaderboardDialog = document.getElementById("leaderboard-dialog");
  const leaderboardList = document.getElementById("leaderboard-list");
  const leaderboardError = document.getElementById("leaderboard-error");
  const leaderboardEmpty = document.getElementById("leaderboard-empty");
  const leaderboardCloseButton = document.getElementById(
    "leaderboard-close-button"
  );
  const viewLeaderboardSplash = document.getElementById(
    "view-leaderboard-button-splash"
  );
  const viewLeaderboardGameOver = document.getElementById(
    "view-leaderboard-button-gameover"
  );

  // Device/standalone body classes + orientation handling (shared with the hub).
  initDeviceClasses();
  if (pauseButton) {
    pauseButton.addEventListener("click", () => {
      if (game.isRunning && !game.isDead) {
        const isPaused = game.togglePause();
        if (isPaused) {
          game.showPauseScreen();
        } else {
          game.hidePauseScreen();
        }
      }
    });
  }

  // Add fullscreen button event listener only for pause dialog
  if (fullscreenButton) {
    fullscreenButton.addEventListener("click", () => {
      if (isIOS()) {
        // iOS-specific: Show add to home screen
        const addToHomeScreen = document.getElementById("add-to-home-screen");
        if (addToHomeScreen) {
          addToHomeScreen.style.display = "flex";
        }
      } else {
        // Standard fullscreen for other devices
        toggleFullScreen();
      }
    });
  }

  // Read the map to play from the URL (?map=<id>). Unknown or missing ids fall
  // back to the default map inside the LevelGenerator, so a bad value is safe.
  const mapId = new URLSearchParams(window.location.search).get("map");

  // A community map ("<username>/<slug>") belongs to another player and isn't in
  // localStorage — fetch it from the cloud and register it into the runtime BEFORE
  // building the game, so the synchronous getMap() path can serve it. We pass the
  // NORMALIZED id (parseShareRef trims/decodes, e.g. a "%2F"-encoded slash) to both
  // registerCommunityMap and Game so getMap() looks up exactly what was registered.
  // On a miss (bad grammar or no such level) we show a friendly splash, not a game.
  let resolvedMapId = mapId;
  const communityRef = parseShareRef(mapId);
  if (communityRef || isCommunityMapId(mapId)) {
    if (!communityRef) {
      showCommunityNotFound(mapId);
      return;
    }

    // Prefer a PUBLISHED level: an immutable snapshot with its own leaderboard.
    // The published-version UUID becomes the runtime/leaderboard map id (it has no
    // "/" and no "custom-" prefix, so isNonLeaderboardMapId() is false → scores are
    // collected). An explicit "@<version>" plays that past version.
    const published = await getPublishedLevel(
      communityRef.username,
      communityRef.slug,
      communityRef.version
    );
    if (published) {
      const { map } = buildRuntimeMap({ ...published, id: published.versionId });
      if (!map) {
        showCommunityNotFound(mapId);
        return;
      }
      registerCommunityMap(published.versionId, map);
      resolvedMapId = published.versionId;
    } else if (communityRef.version != null) {
      // An explicit @version with no matching published version has no draft
      // equivalent — it's a miss.
      showCommunityNotFound(mapId);
      return;
    } else {
      // Not published: fall back to the live DRAFT share (mutable, no leaderboard),
      // exactly as before, so sharing an unpublished work-in-progress still works.
      const stored = await getPublicLevel(
        communityRef.username,
        communityRef.slug
      );
      if (!stored) {
        showCommunityNotFound(mapId);
        return;
      }
      const id = communityMapId(communityRef.username, communityRef.slug);
      const { map } = buildRuntimeMap({ ...stored, id });
      if (!map) {
        showCommunityNotFound(mapId);
        return;
      }
      registerCommunityMap(id, map);
      resolvedMapId = id;
    }
  }

  // Create a new game instance
  const game = new Game(gameContainer, scoreElement, gameOverElement, {
    mapId: resolvedMapId,
  });

  // --- Map-name title card ---
  // Show the active map's name centered when a run begins, then fade it after
  // 5 seconds of *play* (gameTime only advances while running — paused/waiting
  // time doesn't count), or sooner if the run ends first.
  const mapTitle = document.getElementById("map-title");
  const mapTitleText = document.getElementById("map-title-text");
  let mapTitleTimer = null;

  function showMapTitle() {
    if (!mapTitle || !mapTitleText || !game.mapName) return;
    if (mapTitleTimer) clearInterval(mapTitleTimer); // restart on quick re-runs
    mapTitleText.textContent = game.mapName;
    mapTitle.classList.add("is-visible");
    mapTitleTimer = setInterval(() => {
      if (game.gameTime >= 5000 || game.isDead || !game.isRunning) {
        mapTitle.classList.remove("is-visible"); // CSS transition fades it out
        clearInterval(mapTitleTimer);
        mapTitleTimer = null;
      }
    }, 150);
  }

  game.onStart = showMapTitle;

  // --- Slim pre-game / pause overlay wiring ---
  // Level selection, the editor entry, and community linking all live in the
  // main-menu hub now (src/ui/MainMenu.js, shown on a bare URL). The splash is a
  // lean in-game overlay: controls, Start/Resume, Leaderboard (eligible maps),
  // Edit (own levels), and a "Back to Menu" return to the hub.
  const backToMenuButton = document.getElementById("back-to-menu-button");
  if (backToMenuButton) {
    backToMenuButton.addEventListener("click", () => {
      window.location.search = ""; // bare URL → the hub
    });
  }

  // Player-authored and community levels have no shared leaderboard. Two flavors:
  //  • own-custom ("custom-" id) — the player's local level: reveal "Edit Level".
  //  • community ("<username>/<slug>" id) — someone else's shared level: not theirs
  //    to edit, so no Edit button.
  const activeMapId = game.levelGenerator.map.id || "";
  const isOwnCustom = isCustomMapId(activeMapId);
  if (isNonLeaderboardMapId(activeMapId)) {
    document
      .getElementById("view-leaderboard-button-splash")
      ?.classList.add("hidden");
    document
      .getElementById("view-leaderboard-button-gameover")
      ?.classList.add("hidden");
    if (isOwnCustom) {
      const editLevelButton = document.getElementById("edit-level-button");
      if (editLevelButton) {
        editLevelButton.classList.remove("hidden");
        editLevelButton.addEventListener("click", () => {
          window.location.search = "?edit=" + encodeURIComponent(activeMapId);
        });
      }
    }
  }

  // --- Score Submission Callbacks ---
  game.onScoreSubmitted = (result) => {
    if (result && result.scoreId && result.challengeJwt) {
      lastSubmittedScoreId = result.scoreId;
      lastChallengeJwt = result.challengeJwt;
      console.log(
        `Stored score ID: ${lastSubmittedScoreId} and JWT for potential name update.`
      );

      // Display the player name and make the section visible on success
      const currentName = getPlayerName();
      if (playerNameDisplay) {
        playerNameDisplay.textContent = currentName;
      }
      if (playerNameSection) {
        playerNameSection.style.display = ""; // Make visible
      }

      // Remove any previous error message
      const errorSpan = playerNameSection
        ? playerNameSection.querySelector(".submission-error")
        : null;
      if (errorSpan) errorSpan.remove();
      const statsErrorSpan = document
        .getElementById("game-over-stats")
        ?.querySelector(".submission-error");
      if (statsErrorSpan) statsErrorSpan.remove();
    } else {
      console.warn(
        "Score submitted, but details for name update are missing.",
        result
      );
      // Hide name section if details are missing
      if (playerNameSection) playerNameSection.style.display = "none";
    }
  };

  game.onScoreSubmitFailed = (errorMessage) => {
    console.error("Score submission failed:", errorMessage);
    // Hide the name section entirely on failure or score 0
    if (playerNameSection) playerNameSection.style.display = "none";

    // Optionally display an error message near the score stats
    const gameOverStats = document.getElementById("game-over-stats");
    // Remove previous error first
    const existingErrorSpan = gameOverStats
      ? gameOverStats.querySelector(".submission-error")
      : null;
    if (existingErrorSpan) existingErrorSpan.remove();

    if (gameOverStats && errorMessage !== "Score was 0.") {
      // Don't show error for score 0
      let errorSpan = document.createElement("div");
      // A local custom level intentionally skips submission — show a neutral
      // note, not a red "failed" error.
      const isLocalNote =
        isOwnCustom || isCommunity || /^Local level/.test(errorMessage);
      errorSpan.className = isLocalNote
        ? "submission-error opacity-60 text-sm mt-2"
        : "submission-error text-red-500 text-sm mt-2";
      errorSpan.textContent = isLocalNote
        ? errorMessage
        : `Score submission failed: ${errorMessage}`;
      gameOverStats.appendChild(errorSpan);
    }
  };

  // Initialize the game but don't start it yet
  game.init();

  // Start the animation loop in waiting-for-start mode only if not already running
  if (!game.animationFrameId) {
    game.animate(performance.now());
  }

  // Add event listener for start button
  startButton.addEventListener("click", () => {
    // Hide splash screen
    splashScreen.classList.add("hidden");

    // Start playing background music when splash screen is closed
    game.audioManager.playRandomTrack();

    if (game.isPaused) {
      game.resume();
      // Game.resume already handles hiding the pause screen
    } else {
      // Show game elements and display "press to start" message
      game.prepareGameAfterSplashScreen();
    }
  });

  // Add event listener for restart button
  restartButton.addEventListener("click", () => {
    gameOverElement.classList.remove("visible");
    gameOverElement.classList.add("hidden");
    // Reset name section display on restart
    if (playerNameSection) playerNameSection.style.display = "";
    // Remove any submission status message
    const errorSpan = playerNameSection
      ? playerNameSection.querySelector(".submission-error")
      : null;
    if (errorSpan) errorSpan.remove();
    const statsErrorSpan = document
      .getElementById("game-over-stats")
      ?.querySelector(".submission-error");
    if (statsErrorSpan) statsErrorSpan.remove();

    // Reset last submission details
    lastSubmittedScoreId = null;
    lastChallengeJwt = null;

    game.restart();
  });

  // Add event listener for change name button
  if (changeNameButton && playerNameDisplay) {
    changeNameButton.addEventListener("click", async () => {
      const oldName = playerNameDisplay.textContent; // Get name currently shown
      const newName = prompt("Enter your leaderboard name:", oldName);

      if (newName && newName.trim() !== "" && newName.trim() !== oldName) {
        const trimmedNewName = newName.trim();
        const updatedLocally = updatePlayerNameIfCurrent(
          oldName,
          trimmedNewName
        );

        if (updatedLocally) {
          playerNameDisplay.textContent = trimmedNewName; // Update display immediately
          console.log("Player name updated locally to:", trimmedNewName);

          // Now, attempt to update the name on the server
          // Check for ALL required pieces of info (scoreId, JWT)
          if (lastSubmittedScoreId && lastChallengeJwt) {
            console.log(
              `Attempting to update name for score ID: ${lastSubmittedScoreId} on server using SupabaseService.`
            );
            try {
              // Ensure updateSecret is NOT passed here
              const updateResult = await updatePlayerName(
                lastSubmittedScoreId,
                trimmedNewName,
                lastChallengeJwt
              );

              if (!updateResult.success) {
                throw new Error(
                  updateResult.message || "Server name update failed"
                );
              }

              console.log(
                "Server name updated successfully via SupabaseService."
              );
            } catch (error) {
              console.error("Failed to update score name on server:", error);
              // Optional: Inform user, maybe revert local display if server update is critical
              alert(`Failed to update name on server: ${error.message}`);
              // Revert display on failure?
              // playerNameDisplay.textContent = oldName;
              // updatePlayerNameIfCurrent(trimmedNewName, oldName); // Attempt to revert local storage
            }
          } else {
            console.log(
              "No recent score submission details found (scoreId or JWT missing), skipping server update."
            );
            alert(
              "Could not update name on server: submission details missing."
            );
          }
        } else {
          console.warn(
            "Local name update failed (name might have changed elsewhere or new name was invalid)."
          );
          alert(
            "Could not change name locally. It might have been changed in another tab."
          );
          // Refresh displayed name from storage in case it changed
          playerNameDisplay.textContent = getPlayerName();
        }
      } else if (newName === null) {
        console.log("Name change cancelled.");
      } else {
        console.log("Name not changed (new name was empty or same as old).");
      }
    });
  }

  // Add event listeners for controls
  window.addEventListener("keydown", (event) => {
    // ESCAPE Key: Close Leaderboard if it's open
    if (
      event.key === "Escape" &&
      leaderboardDialog &&
      !leaderboardDialog.classList.contains("hidden")
    ) {
      hideLeaderboard();
      event.preventDefault(); // Prevent other escape actions (like closing pause menu)
      return; // Stop further processing for Escape key if leaderboard was closed
    }

    // If splash screen is visible, only allow Enter key, clicking start button, or leaderboard button
    if (!splashScreen.classList.contains("hidden") && !game.isPaused) {
      if (event.code === "Enter") {
        splashScreen.classList.add("hidden");
        game.audioManager.playRandomTrack();

        game.prepareGameAfterSplashScreen();
      }
      // Don't prevent default for other keys if splash is up, allow focus navigation
      // event.preventDefault();
      return;
    }

    // Handle Enter key for game state transitions
    if (event.code === "Enter") {
      // If game is paused, resume it
      if (game.isPaused) {
        game.resume();
        event.preventDefault();
      }
      // If game over screen is visible, restart the game
      else if (gameOverElement.classList.contains("visible")) {
        gameOverElement.classList.remove("visible");
        gameOverElement.classList.add("hidden");
        game.restart();
        event.preventDefault();
      }
      // If waiting to start, start the game (consistent behavior for after splash screen and after restart)
      else if (
        game.waitingForStart &&
        splashScreen.classList.contains("hidden") &&
        !gameOverElement.classList.contains("visible")
      ) {
        game.start();
        event.preventDefault();
      }
      // If game is running and not paused, pause it
      else if (game.isRunning && !game.isDead) {
        const isPaused = game.togglePause();
        if (isPaused) {
          game.showPauseScreen();
        } else {
          game.hidePauseScreen();
        }
        event.preventDefault();
      }
      return; // Skip further processing for Enter key
    }

    // If game is waiting for interaction and splash screen is hidden, start it on any key press
    if (
      game.waitingForStart &&
      splashScreen.classList.contains("hidden") &&
      !gameOverElement.classList.contains("visible")
    ) {
      game.start();
      event.preventDefault();
      // Continue to handle the current key for immediate control
    }

    // Regular game controls - pass to Game's handler
    // Convert event.code to the format expected by handleKeyDown
    let key = event.key.toLowerCase();
    if (event.code === "Space") key = " ";
    if (event.code === "ArrowRight") key = "arrowright";
    if (event.code === "ArrowLeft") key = "arrowleft";
    if (event.code === "ArrowUp") key = "arrowup";
    if (event.code === "ArrowDown") key = "arrowdown";
    if (event.code === "KeyD") key = "d";
    if (event.code === "KeyA") key = "a";

    game.handleKeyDown({ key });
  });

  // Add keyup event listener for jump control and direction keys
  window.addEventListener("keyup", (event) => {
    // Convert event.code to the format expected by handleKeyUp
    let key = event.key.toLowerCase();
    if (event.code === "Space") key = " ";
    if (event.code === "ArrowRight") key = "arrowright";
    if (event.code === "ArrowLeft") key = "arrowleft";
    if (event.code === "ArrowUp") key = "arrowup";
    if (event.code === "ArrowDown") key = "arrowdown";
    if (event.code === "KeyD") key = "d";
    if (event.code === "KeyA") key = "a";

    game.handleKeyUp({ key });
  });

  // Add touch controls for mobile (only when game is running)
  gameContainer.addEventListener("touchstart", (event) => {
    // If splash screen is visible, check if we're touching the start button
    if (!splashScreen.classList.contains("hidden") && !game.isPaused) {
      // Check if the touch was on the start button
      const startButtonRect = startButton.getBoundingClientRect();
      const touchX = event.touches[0].clientX;
      const touchY = event.touches[0].clientY;

      if (
        touchX >= startButtonRect.left &&
        touchX <= startButtonRect.right &&
        touchY >= startButtonRect.top &&
        touchY <= startButtonRect.bottom
      ) {
        // Touch was on start button
        splashScreen.classList.add("hidden");

        // Start playing background music when splash screen is closed
        game.audioManager.playRandomTrack();

        if (game.isPaused) {
          game.resume();
          // Game.resume already handles hiding the pause screen
        } else {
          // Show game elements and display "press to start" message
          game.prepareGameAfterSplashScreen();
        }
      }
      // Prevent touches anywhere else on splash screen from starting the game
      return;
    }

    // If game is waiting for start and splash screen is hidden, start it on any touch
    if (
      game.waitingForStart &&
      splashScreen.classList.contains("hidden") &&
      !gameOverElement.classList.contains("visible")
    ) {
      // Start the game
      game.start();
    } else if (game.isRunning && !game.isPaused) {
      // Simulate space key press for jump on game area touch
      game.handleKeyDown({ key: " " });
    }
  });

  // Add touch end for mobile jump release
  gameContainer.addEventListener("touchend", () => {
    if (game.isRunning && !game.isPaused) {
      // Simulate space key release
      game.handleKeyUp({ key: " " });
    }
  });

  // Mobile controls handling
  const speedUpButton = document.getElementById("speed-up");
  const speedDownButton = document.getElementById("speed-down");

  // Speed control handling
  if (speedUpButton) {
    speedUpButton.addEventListener("touchstart", (e) => {
      e.preventDefault();
      e.stopPropagation(); // Prevent event from bubbling to the container

      // Don't do anything if splash screen is visible
      if (!splashScreen.classList.contains("hidden") && !game.isPaused) {
        return;
      }

      // If waiting for start, start the game and immediately apply controls
      if (
        game.waitingForStart &&
        splashScreen.classList.contains("hidden") &&
        !gameOverElement.classList.contains("visible")
      ) {
        // Start the game first
        game.start();

        // Then immediately apply the control
        game.handleKeyDown({ key: "arrowright" });
      } else if (game.isRunning && !game.isPaused) {
        game.handleKeyDown({ key: "arrowright" });
      }
    });

    speedUpButton.addEventListener("touchend", (e) => {
      e.preventDefault();
      e.stopPropagation(); // Prevent event from bubbling
      if (game.isRunning && !game.isPaused) {
        game.handleKeyUp({ key: "arrowright" });
      }
    });
  }

  if (speedDownButton) {
    speedDownButton.addEventListener("touchstart", (e) => {
      e.preventDefault();
      e.stopPropagation(); // Prevent event from bubbling to the container

      // Don't do anything if splash screen is visible
      if (!splashScreen.classList.contains("hidden") && !game.isPaused) {
        return;
      }

      // If waiting for start, start the game and immediately apply controls
      if (
        game.waitingForStart &&
        splashScreen.classList.contains("hidden") &&
        !gameOverElement.classList.contains("visible")
      ) {
        // Start the game first
        game.start();

        // Then immediately apply the control
        game.handleKeyDown({ key: "arrowleft" });
      } else if (game.isRunning && !game.isPaused) {
        game.handleKeyDown({ key: "arrowleft" });
      }
    });

    speedDownButton.addEventListener("touchend", (e) => {
      e.preventDefault();
      e.stopPropagation(); // Prevent event from bubbling
      if (game.isRunning && !game.isPaused) {
        game.handleKeyUp({ key: "arrowleft" });
      }
    });
  }

  // Fullscreen handling
  const addToHomeScreen = document.getElementById("add-to-home-screen");
  const dismissButton = addToHomeScreen.querySelector(".dismiss-button");

  // Show fullscreen dialog
  function showFullscreenDialog() {
    addToHomeScreen.style.display = "flex";
  }

  // Hide fullscreen dialog
  function hideFullscreenDialog() {
    addToHomeScreen.style.display = "none";
  }

  // Handle dismiss button click
  dismissButton.addEventListener("click", () => {
    hideFullscreenDialog();
  });

  // Handle visibility change
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      // Game is hidden, pause it
      if (!game.isPaused) {
        game.pause();
      }
    }
  });

  // Remove the loading overlay when everything is loaded
  const loadingOverlay = document.getElementById("loading-overlay");
  if (loadingOverlay) {
    // Hide the loading overlay
    loadingOverlay.classList.add("opacity-0");
    // Remove it after a transition would have completed
    setTimeout(() => {
      loadingOverlay.classList.add("hidden");
    }, 500);
  }

  // --- Leaderboard Functions ---
  async function showLeaderboard() {
    if (
      !leaderboardDialog ||
      !leaderboardList ||
      !leaderboardError ||
      !leaderboardEmpty
    )
      return;

    // Show loading state
    leaderboardList.innerHTML = '<li class="opacity-50">Loading...</li>';
    leaderboardError.classList.add("hidden");
    leaderboardEmpty.classList.add("hidden");
    leaderboardDialog.classList.remove("hidden");
    leaderboardDialog.classList.add("flex");

    // Scope the board to the active map and label it with the map name.
    const activeMapId = game.levelGenerator.map.id;
    const leaderboardMapName = document.getElementById("leaderboard-map-name");
    if (leaderboardMapName) {
      leaderboardMapName.textContent = game.mapName || "";
    }

    try {
      const scores = await getLeaderboard(10, activeMapId); // Fetch top 10 for this map

      leaderboardList.innerHTML = ""; // Clear loading/previous scores

      if (scores && scores.length > 0) {
        scores.forEach((score, index) => {
          const div = document.createElement("div");
          div.className = "flex justify-between w-full";
          const playerName = score.player_name || "Anonymous";
          div.innerHTML = `<span class="mr-12">${
            index + 1
          }. ${playerName}</span><span>${score.score}</span>`;
          leaderboardList.appendChild(div);
        });
      } else if (scores) {
        // Successfully fetched, but no scores
        leaderboardEmpty.classList.remove("hidden");
      } else {
        // getLeaderboard returned null or undefined (error handled in SupabaseService?)
        throw new Error("Failed to fetch scores.");
      }
    } catch (error) {
      console.error("Error fetching or displaying leaderboard:", error);
      leaderboardList.innerHTML = ""; // Clear list
      leaderboardError.textContent = `Error: ${
        error.message || "Could not load leaderboard."
      }`;
      leaderboardError.classList.remove("hidden");
    }
  }

  function hideLeaderboard() {
    if (leaderboardDialog) {
      leaderboardDialog.classList.add("hidden");
      leaderboardDialog.classList.remove("flex");
    }
  }

  // Leaderboard Buttons
  if (viewLeaderboardSplash) {
    viewLeaderboardSplash.addEventListener("click", showLeaderboard);
  }
  if (viewLeaderboardGameOver) {
    viewLeaderboardGameOver.addEventListener("click", showLeaderboard);
  }
  if (leaderboardCloseButton) {
    leaderboardCloseButton.addEventListener("click", hideLeaderboard);
  }
});
